import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarDays, Copy, Crown, ImagePlus, Megaphone, Pencil, Plus, Sparkles, Trash2, Upload, X } from "lucide-react";
import api from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const PLATFORMS = [
  { value: "instagram", label: "Instagram" },
  { value: "facebook", label: "Facebook" },
  { value: "story", label: "Story" },
];

const BANNER_TYPES = [
  { value: "info", label: "Information" },
  { value: "promo", label: "Promotion" },
  { value: "evenement", label: "Événement" },
];

const emptyPost = { date: "", time: "", platform: "instagram", text: "", image: "", status: "a_publier" };
const emptyBanner = { text: "", link: "", type: "info", starts_at: "", ends_at: "", active: true };

export default function MarketingManager() {
  const [sub, setSub] = useState("affiches");
  const [posterPrompt, setPosterPrompt] = useState("");
  const [posterAspect, setPosterAspect] = useState("portrait");
  const [poster, setPoster] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [postTopic, setPostTopic] = useState("");
  const [postPlatform, setPostPlatform] = useState("instagram");
  const [postText, setPostText] = useState("");
  const [writingPost, setWritingPost] = useState(false);
  const [posts, setPosts] = useState([]);
  const [editingPost, setEditingPost] = useState(null);
  const [banners, setBanners] = useState([]);
  const [editingBanner, setEditingBanner] = useState(null);
  const [eventMode, setEventMode] = useState({ enabled: false, title: "", subtitle: "", starts_at: "", ends_at: "" });
  const [saving, setSaving] = useState(false);

  const backend = process.env.REACT_APP_BACKEND_URL;

  const load = () => {
    api.get("/admin/marketing/posts").then((r) => setPosts(r.data)).catch(() => {});
    api.get("/admin/banners").then((r) => setBanners(r.data)).catch(() => {});
    api
      .get("/settings")
      .then((r) => setEventMode({ enabled: false, title: "", subtitle: "", starts_at: "", ends_at: "", ...(r.data?.event_mode || {}) }))
      .catch(() => {});
  };
  useEffect(() => {
    load();
  }, []);

  const generatePoster = async (e) => {
    e.preventDefault();
    if (!posterPrompt.trim()) {
      toast.error("Décrivez l'affiche à générer.");
      return;
    }
    setGenerating(true);
    setPoster(null);
    try {
      const { data } = await api.post("/admin/marketing/poster", { prompt: posterPrompt, aspect: posterAspect }, { timeout: 120000 });
      setPoster(data);
      toast.success("Affiche générée et enregistrée dans la médiathèque.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "La génération a échoué.");
    } finally {
      setGenerating(false);
    }
  };

  const generateText = async (e) => {
    e.preventDefault();
    if (!postTopic.trim()) {
      toast.error("Décrivez le sujet de la publication.");
      return;
    }
    setWritingPost(true);
    setPostText("");
    try {
      const { data } = await api.post("/admin/marketing/post-text", { topic: postTopic, platform: postPlatform }, { timeout: 90000 });
      setPostText(data.text);
    } catch (err) {
      toast.error(err.response?.data?.detail || "La génération a échoué.");
    } finally {
      setWritingPost(false);
    }
  };

  const copyText = () => {
    navigator.clipboard.writeText(postText);
    toast.success("Texte copié — prêt à publier.");
  };

  const savePost = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingPost.id) {
        await api.put(`/admin/marketing/posts/${editingPost.id}`, editingPost);
      } else {
        await api.post("/admin/marketing/posts", editingPost);
      }
      toast.success("Publication programmée.");
      setEditingPost(null);
      load();
    } catch {
      toast.error("Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  const removePost = async (id) => {
    await api.delete(`/admin/marketing/posts/${id}`);
    toast.success("Publication supprimée.");
    load();
  };

  const saveBanner = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingBanner.id) {
        await api.put(`/admin/banners/${editingBanner.id}`, editingBanner);
      } else {
        await api.post("/admin/banners", editingBanner);
      }
      toast.success("Bannière enregistrée.");
      setEditingBanner(null);
      load();
    } catch {
      toast.error("Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  const removeBanner = async (id) => {
    await api.delete(`/admin/banners/${id}`);
    toast.success("Bannière supprimée.");
    load();
  };

  const saveEventMode = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { event_mode: eventMode });
      toast.success("Mode Événement enregistré.");
    } catch {
      toast.error("Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="marketing-manager">
      <h2 className="mb-2 text-lg font-semibold text-ivory">Marketing</h2>
      <p className="mb-8 max-w-2xl text-xs leading-relaxed text-ivory/40">
        Générez vos affiches et textes de publications par IA (inclus dans votre abonnement),
        programmez vos publications et vos bannières. Vérifiez toujours les détails (dates,
        coordonnées) des contenus générés avant publication.
      </p>

      <div className="mb-8 flex flex-wrap gap-2" data-testid="marketing-subtabs">
        {[
          { id: "une", label: "À la une (VIP)", icon: Crown },
          { id: "affiches", label: "Affiches IA", icon: ImagePlus },
          { id: "publications", label: `Publications (${posts.length})`, icon: Megaphone },
          { id: "bannieres", label: `Bannières (${banners.length})`, icon: Sparkles },
          { id: "evenement", label: "Mode Événement", icon: CalendarDays },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setSub(t.id)}
            data-testid={`marketing-subtab-${t.id}`}
            className={`flex items-center gap-2 rounded-md border px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.15em] transition-colors ${
              sub === t.id ? "border-copper bg-copper text-ivory" : "border-white/10 text-ivory/60 hover:border-copper/50 hover:text-ivory"
            }`}
          >
            <t.icon className="h-3.5 w-3.5" strokeWidth={1.5} />
            {t.label}
          </button>
        ))}
      </div>

      {sub === "une" && <VipBannerAdmin />}

      {sub === "affiches" && (
        <div className="grid gap-8 lg:grid-cols-2" data-testid="poster-panel">
          <div className="space-y-6 rounded-lg border border-white/10 bg-coal/60 p-8">
            <form onSubmit={generatePoster} className="space-y-4">
              <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-gold">Générer une affiche</h3>
              <textarea
                rows={3}
                value={posterPrompt}
                onChange={(e) => setPosterPrompt(e.target.value)}
                placeholder="Ex : Soirée braai samedi soir, viandes grillées, ambiance chaleureuse"
                className="input-dark !h-auto py-3"
                data-testid="poster-prompt-input"
              />
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Format</label>
                <Select value={posterAspect} onValueChange={setPosterAspect}>
                  <SelectTrigger className="!h-11 border-white/10 bg-white/5 text-sm text-ivory" data-testid="poster-aspect-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-white/10 bg-coal text-ivory">
                    <SelectItem value="portrait">Affiche (4:5)</SelectItem>
                    <SelectItem value="story">Story (9:16)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <button type="submit" disabled={generating} className="btn-primary w-full" data-testid="poster-generate-button">
                <ImagePlus className="h-4 w-4" strokeWidth={1.5} />
                {generating ? "Génération en cours (30-60 s)…" : "Générer l'affiche"}
              </button>
            </form>

            <form onSubmit={generateText} className="space-y-4 border-t border-white/10 pt-6">
              <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-gold">Générer un texte de publication</h3>
              <input
                value={postTopic}
                onChange={(e) => setPostTopic(e.target.value)}
                placeholder="Ex : Nouveau jus de bissap gingembre en boutique"
                className="input-dark !h-11"
                data-testid="post-topic-input"
              />
              <Select value={postPlatform} onValueChange={setPostPlatform}>
                <SelectTrigger className="!h-11 border-white/10 bg-white/5 text-sm text-ivory" data-testid="post-platform-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-white/10 bg-coal text-ivory">
                  {PLATFORMS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <button type="submit" disabled={writingPost} className="btn-outline w-full" data-testid="post-generate-button">
                <Sparkles className="h-4 w-4" strokeWidth={1.5} />
                {writingPost ? "Rédaction…" : "Rédiger la publication"}
              </button>
              {postText && (
                <div className="rounded-md border border-white/10 bg-ink/60 p-4" data-testid="post-text-result">
                  <p className="whitespace-pre-line text-sm leading-relaxed text-ivory/80">{postText}</p>
                  <button type="button" onClick={copyText} className="btn-outline mt-4 !h-9 !px-4 !text-[11px]" data-testid="post-copy-button">
                    <Copy className="h-3.5 w-3.5" strokeWidth={1.5} />
                    Copier le texte
                  </button>
                </div>
              )}
            </form>
          </div>

          <div className="rounded-lg border border-white/10 bg-coal/60 p-8" data-testid="poster-preview-panel">
            <h3 className="mb-4 text-sm font-semibold uppercase tracking-[0.2em] text-gold">Aperçu</h3>
            {generating ? (
              <div className="flex aspect-[4/5] items-center justify-center rounded-md border border-dashed border-white/15 text-sm text-ivory/40">
                Génération de votre affiche…
              </div>
            ) : poster ? (
              <div>
                <img src={`${backend}/api/media/${poster.id}/file`} alt="Affiche générée" className="w-full rounded-md border border-white/10" data-testid="poster-preview" />
                <a
                  href={`${backend}/api/media/${poster.id}/file`}
                  download={poster.filename}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary mt-4 w-full"
                  data-testid="poster-download"
                >
                  Télécharger l'affiche
                </a>
              </div>
            ) : (
              <div className="flex aspect-[4/5] items-center justify-center rounded-md border border-dashed border-white/15 text-center text-sm text-ivory/40">
                Votre affiche apparaîtra ici.<br />Décrivez-la et cliquez sur « Générer ».
              </div>
            )}
          </div>
        </div>
      )}

      {sub === "publications" && (
        <div data-testid="posts-panel">
          <div className="mb-6 flex justify-end">
            <button onClick={() => setEditingPost({ ...emptyPost })} className="btn-primary !h-10 !px-5 !text-[11px]" data-testid="post-add-button">
              <Plus className="h-4 w-4" strokeWidth={1.5} />
              Programmer une publication
            </button>
          </div>
          {editingPost && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" data-testid="post-edit-modal">
              <form onSubmit={savePost} className="max-h-[90vh] w-full max-w-xl space-y-4 overflow-y-auto rounded-lg border border-white/10 bg-coal p-8">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-semibold text-ivory">Publication programmée</h3>
                  <button type="button" onClick={() => setEditingPost(null)} aria-label="Fermer">
                    <X className="h-5 w-5 text-ivory/50 hover:text-ivory" strokeWidth={1.5} />
                  </button>
                </div>
                <div className="grid gap-4 sm:grid-cols-3">
                  <input required type="date" value={editingPost.date} onChange={(e) => setEditingPost((p) => ({ ...p, date: e.target.value }))} className="input-dark !h-11 [color-scheme:dark]" data-testid="post-date-input" />
                  <input value={editingPost.time} onChange={(e) => setEditingPost((p) => ({ ...p, time: e.target.value }))} placeholder="18:00" className="input-dark !h-11" data-testid="post-time-input" />
                  <Select value={editingPost.platform} onValueChange={(v) => setEditingPost((p) => ({ ...p, platform: v }))}>
                    <SelectTrigger className="!h-11 border-white/10 bg-white/5 text-sm text-ivory" data-testid="post-edit-platform-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="border-white/10 bg-coal text-ivory">
                      {PLATFORMS.map((p) => (
                        <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <textarea rows={5} value={editingPost.text} onChange={(e) => setEditingPost((p) => ({ ...p, text: e.target.value }))} placeholder="Texte de la publication (générez-le dans l'onglet Affiches IA puis collez-le ici)" className="input-dark !h-auto py-3" data-testid="post-text-input" />
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Statut</label>
                  <Select value={editingPost.status} onValueChange={(v) => setEditingPost((p) => ({ ...p, status: v }))}>
                    <SelectTrigger className="!h-11 border-white/10 bg-white/5 text-sm text-ivory" data-testid="post-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="border-white/10 bg-coal text-ivory">
                      <SelectItem value="a_publier">À publier</SelectItem>
                      <SelectItem value="publie">Publiée</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <button type="submit" disabled={saving} className="btn-primary w-full" data-testid="post-save-button">
                  {saving ? "Enregistrement…" : "Enregistrer"}
                </button>
              </form>
            </div>
          )}
          {posts.length === 0 ? (
            <p className="text-sm text-ivory/40" data-testid="posts-empty">
              Aucune publication programmée. Générez un texte dans « Affiches IA » puis programmez-le ici.
            </p>
          ) : (
            <div className="space-y-2" data-testid="posts-list">
              {posts.map((p) => (
                <div key={p.id} className="flex flex-wrap items-center gap-x-5 gap-y-2 rounded-lg border border-white/10 bg-coal/60 px-5 py-3.5" data-testid={`post-row-${p.id}`}>
                  <span className="text-sm text-ivory">{p.date}{p.time ? ` · ${p.time}` : ""}</span>
                  <span className="rounded border border-gold/30 px-2 py-0.5 text-[11px] font-semibold capitalize text-gold">{p.platform}</span>
                  <span className="min-w-0 flex-1 truncate text-xs text-ivory/50">{p.text || "—"}</span>
                  <span className={`text-[10px] font-semibold uppercase tracking-wider ${p.status === "publie" ? "text-gold" : "text-copper"}`}>
                    {p.status === "publie" ? "Publiée" : "À publier"}
                  </span>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setEditingPost({ ...p })} aria-label="Modifier" className="flex h-8 w-8 items-center justify-center rounded-md border border-white/10 text-ivory/50 transition-colors hover:border-gold hover:text-gold" data-testid={`post-edit-${p.id}`}>
                      <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
                    </button>
                    <button onClick={() => removePost(p.id)} aria-label="Supprimer" className="flex h-8 w-8 items-center justify-center rounded-md border border-white/10 text-ivory/40 transition-colors hover:border-red-400/50 hover:text-red-400" data-testid={`post-delete-${p.id}`}>
                      <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {sub === "bannieres" && (
        <div data-testid="banners-panel">
          <div className="mb-6 flex justify-end">
            <button onClick={() => setEditingBanner({ ...emptyBanner })} className="btn-primary !h-10 !px-5 !text-[11px]" data-testid="banner-add-button">
              <Plus className="h-4 w-4" strokeWidth={1.5} />
              Créer une bannière
            </button>
          </div>
          {editingBanner && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" data-testid="banner-edit-modal">
              <form onSubmit={saveBanner} className="max-h-[90vh] w-full max-w-xl space-y-4 overflow-y-auto rounded-lg border border-white/10 bg-coal p-8">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-semibold text-ivory">Bannière du site</h3>
                  <button type="button" onClick={() => setEditingBanner(null)} aria-label="Fermer">
                    <X className="h-5 w-5 text-ivory/50 hover:text-ivory" strokeWidth={1.5} />
                  </button>
                </div>
                <input required value={editingBanner.text} onChange={(e) => setEditingBanner((b) => ({ ...b, text: e.target.value }))} placeholder="Ex : -20 % sur le Comptoir ce week-end !" className="input-dark !h-11" data-testid="banner-text-input" />
                <input value={editingBanner.link} onChange={(e) => setEditingBanner((b) => ({ ...b, link: e.target.value }))} placeholder="Lien (optionnel, ex : /comptoir)" className="input-dark !h-11" data-testid="banner-link-input" />
                <div className="grid gap-4 sm:grid-cols-3">
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Type</label>
                    <Select value={editingBanner.type} onValueChange={(v) => setEditingBanner((b) => ({ ...b, type: v }))}>
                      <SelectTrigger className="!h-11 border-white/10 bg-white/5 text-sm text-ivory" data-testid="banner-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="border-white/10 bg-coal text-ivory">
                        {BANNER_TYPES.map((t) => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Début</label>
                    <input type="date" value={editingBanner.starts_at} onChange={(e) => setEditingBanner((b) => ({ ...b, starts_at: e.target.value }))} className="input-dark !h-11 [color-scheme:dark]" data-testid="banner-starts-input" />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Fin</label>
                    <input type="date" value={editingBanner.ends_at} onChange={(e) => setEditingBanner((b) => ({ ...b, ends_at: e.target.value }))} className="input-dark !h-11 [color-scheme:dark]" data-testid="banner-ends-input" />
                  </div>
                </div>
                <label className="flex items-center gap-3 text-sm text-ivory/70">
                  Active
                  <Switch checked={editingBanner.active} onCheckedChange={(v) => setEditingBanner((b) => ({ ...b, active: v }))} data-testid="banner-active-switch" />
                </label>
                <button type="submit" disabled={saving} className="btn-primary w-full" data-testid="banner-save-button">
                  {saving ? "Enregistrement…" : "Enregistrer"}
                </button>
              </form>
            </div>
          )}
          {banners.length === 0 ? (
            <p className="text-sm text-ivory/40" data-testid="banners-empty">
              Aucune bannière. Les bannières actives s'affichent en haut du site pendant leur période.
            </p>
          ) : (
            <div className="space-y-2" data-testid="banners-list">
              {banners.map((b) => (
                <div key={b.id} className="flex flex-wrap items-center gap-x-5 gap-y-2 rounded-lg border border-white/10 bg-coal/60 px-5 py-3.5" data-testid={`banner-row-${b.id}`}>
                  <span className={`min-w-0 flex-1 truncate text-sm ${b.active ? "text-ivory" : "text-ivory/35 line-through"}`}>{b.text}</span>
                  <span className="rounded border border-gold/30 px-2 py-0.5 text-[11px] text-gold">
                    {BANNER_TYPES.find((t) => t.value === b.type)?.label}
                  </span>
                  {(b.starts_at || b.ends_at) && (
                    <span className="text-xs text-ivory/40">{b.starts_at || "…"} → {b.ends_at || "…"}</span>
                  )}
                  <div className="flex items-center gap-2">
                    <button onClick={() => setEditingBanner({ ...b })} aria-label="Modifier" className="flex h-8 w-8 items-center justify-center rounded-md border border-white/10 text-ivory/50 transition-colors hover:border-gold hover:text-gold" data-testid={`banner-edit-${b.id}`}>
                      <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
                    </button>
                    <button onClick={() => removeBanner(b.id)} aria-label="Supprimer" className="flex h-8 w-8 items-center justify-center rounded-md border border-white/10 text-ivory/40 transition-colors hover:border-red-400/50 hover:text-red-400" data-testid={`banner-delete-${b.id}`}>
                      <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {sub === "evenement" && (
        <div className="max-w-2xl space-y-5 rounded-lg border border-white/10 bg-coal/60 p-8" data-testid="event-mode-panel">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-gold">Mode Événement</h3>
              <p className="mt-2 text-xs leading-relaxed text-ivory/40">
                Affiche un bandeau spécial sur la page d'accueil du restaurant pendant votre soirée
                ou événement (annonce mise en avant avec dates automatiques).
              </p>
            </div>
            <Switch checked={!!eventMode.enabled} onCheckedChange={(v) => setEventMode((m) => ({ ...m, enabled: v }))} data-testid="event-mode-switch" />
          </div>
          <input value={eventMode.title} onChange={(e) => setEventMode((m) => ({ ...m, title: e.target.value }))} placeholder="Titre (ex : Soirée Braai & Live Music)" className="input-dark !h-11" data-testid="event-mode-title-input" />
          <input value={eventMode.subtitle} onChange={(e) => setEventMode((m) => ({ ...m, subtitle: e.target.value }))} placeholder="Sous-titre (ex : Samedi 24 août dès 19h — réservation conseillée)" className="input-dark !h-11" data-testid="event-mode-subtitle-input" />
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Début</label>
              <input type="date" value={eventMode.starts_at} onChange={(e) => setEventMode((m) => ({ ...m, starts_at: e.target.value }))} className="input-dark !h-11 [color-scheme:dark]" data-testid="event-mode-starts-input" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Fin</label>
              <input type="date" value={eventMode.ends_at} onChange={(e) => setEventMode((m) => ({ ...m, ends_at: e.target.value }))} className="input-dark !h-11 [color-scheme:dark]" data-testid="event-mode-ends-input" />
            </div>
          </div>
          <button onClick={saveEventMode} disabled={saving} className="btn-primary" data-testid="event-mode-save-button">
            {saving ? "Enregistrement…" : "Enregistrer le mode Événement"}
          </button>
        </div>
      )}
    </div>
  );
}

const VIP_STATUS = {
  en_attente_paiement: "Paiement en attente",
  confirmee: "Payée — confirmée",
  abandonnee: "Abandonnée",
};

function VipBannerAdmin() {
  const [banner, setBanner] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [closed, setClosed] = useState(false);
  const [saving, setSaving] = useState(false);
  const backend = process.env.REACT_APP_BACKEND_URL;

  const load = () => {
    api.get("/admin/soiree-vip").then((r) => {
      setBanner(r.data.banner);
      setReservations(r.data.reservations || []);
      setClosed(r.data.closed);
    }).catch(() => {});
  };
  useEffect(() => {
    load();
  }, []);

  const upload = async (file, field) => {
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/media", fd);
      setBanner((b) => ({ ...b, [field]: `${backend}/api/media/${data.id}/file` }));
      toast.success(field === "video_url" ? "Vidéo envoyée." : "Photo envoyée.");
    } catch {
      toast.error("Envoi impossible (vérifiez la taille du fichier).");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/soiree-vip/banner", banner);
      setBanner(data);
      toast.success("Bannière « À la une » enregistrée.");
    } catch {
      toast.error("Enregistrement impossible.");
    } finally {
      setSaving(false);
    }
  };

  if (!banner) return <p className="text-sm text-ivory/40">Chargement…</p>;

  return (
    <div className="grid gap-8 lg:grid-cols-2" data-testid="vip-banner-admin">
      <div className="space-y-5 rounded-lg border border-white/10 bg-coal/60 p-8">
        <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-gold">Bannière « À la une » — page d'accueil</h3>
        <p className="text-xs leading-relaxed text-ivory/40">
          Affichée sur la page d'accueil Univers, entre la photo d'ouverture et « Choisissez votre
          expérience ». Cliquable vers le parcours « Réservez ma table VIP ». Elle se masque
          automatiquement après la soirée (11 octobre 2026 au matin). Les réservations se clôturent
          automatiquement le 10 octobre à 15 h.
        </p>
        <label className="flex items-center justify-between text-sm text-ivory/80">
          Opération active
          <Switch checked={!!banner.enabled} onCheckedChange={(v) => setBanner((b) => ({ ...b, enabled: v }))} data-testid="vip-banner-enabled" />
        </label>
        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Titre</label>
          <input value={banner.title} onChange={(e) => setBanner((b) => ({ ...b, title: e.target.value }))} className="input-dark !h-11" data-testid="vip-banner-title-input" />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Texte</label>
          <input value={banner.text} onChange={(e) => setBanner((b) => ({ ...b, text: e.target.value }))} className="input-dark !h-11" data-testid="vip-banner-text-input" />
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="btn-outline flex !h-11 cursor-pointer items-center justify-center gap-2 !text-[11px]" data-testid="vip-banner-photo-upload">
            <Upload className="h-4 w-4" strokeWidth={1.5} />
            {banner.image_url ? "Changer la photo" : "Téléverser la photo"}
            <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0], "image_url")} />
          </label>
          <label className="btn-outline flex !h-11 cursor-pointer items-center justify-center gap-2 !text-[11px]" data-testid="vip-banner-video-upload">
            <Upload className="h-4 w-4" strokeWidth={1.5} />
            {banner.video_url ? "Changer la vidéo" : "Téléverser une vidéo"}
            <input type="file" accept="video/*" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0], "video_url")} />
          </label>
        </div>
        {banner.image_url && <img src={banner.image_url} alt="Aperçu" className="h-32 w-full rounded-md border border-white/10 object-cover" data-testid="vip-banner-preview" />}
        {banner.video_url && <video src={banner.video_url} muted loop autoPlay playsInline className="h-32 w-full rounded-md border border-white/10 object-cover" data-testid="vip-banner-video-preview" />}
        {banner.video_url && (
          <button onClick={() => setBanner((b) => ({ ...b, video_url: "" }))} className="text-xs text-red-400/80 hover:text-red-400" data-testid="vip-banner-video-remove">
            Retirer la vidéo (la photo sera utilisée)
          </button>
        )}
        <button onClick={save} disabled={saving} className="btn-primary" data-testid="vip-banner-save">
          {saving ? "Enregistrement…" : "Enregistrer la bannière"}
        </button>
      </div>

      <div className="rounded-lg border border-white/10 bg-coal/60 p-8" data-testid="vip-reservations-admin">
        <h3 className="mb-1 text-sm font-semibold uppercase tracking-[0.2em] text-gold">
          Réservations VIP ({reservations.filter((r) => r.status === "confirmee").length}/2 tables)
        </h3>
        <p className="mb-5 text-xs text-ivory/40">
          {closed ? "Réservations clôturées (10 octobre, 15 h)." : "Clôture automatique le samedi 10 octobre 2026 à 15 h."}
        </p>
        {reservations.length === 0 ? (
          <p className="text-sm text-ivory/40" data-testid="vip-resa-empty">Aucune réservation VIP pour le moment.</p>
        ) : (
          <div className="space-y-2" data-testid="vip-resa-list">
            {reservations.map((r) => (
              <div key={r.id} className="rounded-md border border-white/10 px-4 py-3" data-testid={`vip-resa-${r.id}`}>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                  <span className="text-sm font-semibold text-ivory">{r.table_label}</span>
                  <span className={`text-[11px] font-semibold uppercase ${r.status === "confirmee" ? "text-gold" : r.status === "en_attente_paiement" ? "text-copper" : "text-ivory/35"}`}>
                    {VIP_STATUS[r.status] || r.status}
                  </span>
                  <span className="ml-auto text-xs text-ivory/40">
                    {r.deposit} € payés · solde {r.balance_due} € sur place
                  </span>
                </div>
                <p className="mt-1 text-xs text-ivory/60">{r.name} · {r.email} · {r.phone}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
